import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
} from "reactstrap";
import api from "../../config/api";
import { useAppDialog } from "./AppDialogProvider";

const REFRESH_INTERVAL_MS = 60000;

export default function Unreleased({ onBack, onOpenReleasePart }) {
  const dialog = useAppDialog();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKey, setSelectedRowKey] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const searchInputRef = useRef(null);
  const rowRefs = useRef({});
  const mountedRef = useRef(true);

  const selectedRow = useMemo(() => {
    return rows.find((row) => getRowKey(row) === selectedRowKey) || null;
  }, [rows, selectedRowKey]);

  const selectedPart = useMemo(() => {
    if (!selectedRow) {
      return {
        partNum: "",
        rev: 0,
        revMinor: 0,
      };
    }

    return {
      partNum: String(selectedRow.partNumber ?? ""),
      rev: Number(selectedRow.printRevision ?? 0),
      revMinor: Number(selectedRow.mafiaRevision ?? 0),
    };
  }, [selectedRow]);

  const displayRows = useMemo(() => {
    const search = searchText.trim().toUpperCase();
    if (!search) return rows;

    return rows.filter((row) => {
      const partNumber = String(row.partNumber ?? "").toUpperCase();
      const printRevision = String(row.printRevision ?? "").toUpperCase();
      const mafiaRevision = String(row.mafiaRevision ?? "").toUpperCase();
      const by = String(row.by ?? "").toUpperCase();
      const timeStamp = formatDateTime(row.timeStamp).toUpperCase();

      return (
        partNumber.includes(search) ||
        printRevision.includes(search) ||
        mafiaRevision.includes(search) ||
        by.includes(search) ||
        timeStamp.includes(search)
      );
    });
  }, [rows, searchText]);

  const refreshData = useCallback(
    async (showSpinner = true) => {
      try {
        if (showSpinner) {
          setRefreshing(true);
        }

        const data = await getUnreleasedParts();
        if (!mountedRef.current) return;

        const safeRows = data || [];
        setRows(safeRows);

        if (selectedRowKey) {
          const stillExists = safeRows.some((row) => getRowKey(row) === selectedRowKey);
          if (!stillExists) {
            setSelectedRowKey("");
          }
        }
      } catch (error) {
        console.error("Failed to refresh unreleased parts:", error);
        if (showSpinner) {
          await dialog.error("Failed to refresh unreleased parts.");
        }
      } finally {
        if (mountedRef.current && showSpinner) {
          setRefreshing(false);
        }
      }
    },
    [selectedRowKey, dialog]
  );

  useEffect(() => {
    mountedRef.current = true;

    async function loadInitial() {
      try {
        const data = await getUnreleasedParts();
        if (!mountedRef.current) return;
        setRows(data || []);
      } catch (error) {
        console.error("Failed to load unreleased parts:", error);
        await dialog.error("Failed to load unreleased parts list.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
          }
        }
      }
    }

    loadInitial();

    return () => {
      mountedRef.current = false;
    };
  }, [dialog]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      refreshData(false);
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timerId);
  }, [refreshData]);

  function handleRowSelect(row) {
    if (!row) return;
    setSelectedRowKey(getRowKey(row));
  }

  async function openReleasePart(partData = selectedPart) {
    if (!partData.partNum) {
      await dialog.warning("Please select a part first.");
      return;
    }

    try {
      if (typeof onOpenReleasePart === "function") {
        onOpenReleasePart({
          partNum: partData.partNum,
          rev: partData.rev,
          revMinor: partData.revMinor,
        });
      } else {
        console.warn("Release Part callback is not configured.");
        await dialog.error("Release Part page is not configured.");
      }
    } catch (error) {
      console.error("Failed to open release part:", error);
      await dialog.error("Failed to open release part.");
    }
  }

  function handleSearchKeyDown(e) {
    if (e.key !== "Enter") return;

    if (displayRows.length > 0) {
      const firstMatch = displayRows[0];
      const rowKey = getRowKey(firstMatch);

      setSelectedRowKey(rowKey);

      const rowEl = rowRefs.current[rowKey];
      if (rowEl) {
        rowEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }

    if (searchInputRef.current) {
      searchInputRef.current.select();
    }
  }

  return (
    <div className="unreleased-page">
      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="unreleased-toolbar-main">
                <div className="unreleased-toolbar-main-left">
                  <Button color="soft-dark" className="unreleased-action-btn" onClick={onBack}>
                    <i className="ri-arrow-left-line unreleased-btn-icon me-1" />
                    Back
                  </Button>

                  <Button
                    color="success"
                    className="unreleased-action-btn"
                    onClick={() => openReleasePart()}
                    disabled={!selectedPart.partNum}
                  >
                    <i className="ri-rocket-line unreleased-btn-icon me-1" />
                    Open Release Part
                  </Button>
                </div>

                <div className="unreleased-toolbar-main-right">
                  <InputGroup className="unreleased-search-group">
                    <InputGroupText className="bg-light border-end-0">
                      <i className="ri-search-line" />
                    </InputGroupText>
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="border-start-0"
                      placeholder="Search part number, revision, or user"
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="unreleased-toolbar-utility">
                <Button
                  color="primary"
                  className="unreleased-action-btn unreleased-utility-btn"
                  onClick={() => refreshData(true)}
                  disabled={refreshing}
                >
                  <i className="ri-refresh-line unreleased-btn-icon me-1" />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>

                <Badge color="light" className="unreleased-queue-badge">
                  Queue Size: {displayRows.length}
                </Badge>

                <div className="unreleased-info-pill">
                  <span className="unreleased-info-label">Selected Part</span>
                  <span
                    className="unreleased-info-value"
                    title={selectedRow?.partNumber || "No selection"}
                  >
                    {selectedRow?.partNumber || "No selection"}
                  </span>
                </div>

                <div className="unreleased-info-pill">
                  <span className="unreleased-info-label">Revision</span>
                  <span className="unreleased-info-value">
                    {selectedRow
                      ? `${selectedRow.printRevision ?? 0}.${selectedRow.mafiaRevision ?? 0}`
                      : "—"}
                  </span>
                </div>

                <div className="unreleased-refresh-note">
                  Auto refresh every 1 min
                </div>
              </div>
            </CardHeader>

            <CardBody className="p-4">
              <div className="unreleased-table-shell">
                <div className="unreleased-table-scroll">
                  <Table responsive={false} className="align-middle mb-0 unreleased-table">
                    <thead>
                      <tr>
                        <th className="unreleased-col-select">Select</th>
                        <th>Part Number</th>
                        <th>Print Revision</th>
                        <th>MAFIA Revision</th>
                        <th>By</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={6} className="text-center py-5 text-muted">
                            Loading unreleased parts...
                          </td>
                        </tr>
                      ) : displayRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-5 text-muted">
                            No unreleased parts found.
                          </td>
                        </tr>
                      ) : (
                        displayRows.map((row) => {
                          const rowKey = getRowKey(row);
                          const isSelected = rowKey === selectedRowKey;

                          return (
                            <tr
                              key={rowKey}
                              ref={(el) => {
                                if (el) {
                                  rowRefs.current[rowKey] = el;
                                }
                              }}
                              tabIndex={0}
                              onClick={() => handleRowSelect(row)}
                              onDoubleClick={() =>
                                openReleasePart({
                                  partNum: String(row.partNumber ?? ""),
                                  rev: Number(row.printRevision ?? 0),
                                  revMinor: Number(row.mafiaRevision ?? 0),
                                })
                              }
                              className={`unreleased-data-row ${isSelected ? "selected" : ""}`}
                            >
                              <td className="unreleased-col-select">
                                <Input
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => handleRowSelect(row)}
                                  name="selected-unreleased-part-row"
                                />
                              </td>
                              <td>
                                <div className="fw-semibold text-dark">{row.partNumber}</div>
                              </td>
                              <td>{row.printRevision}</td>
                              <td>{row.mafiaRevision}</td>
                              <td>{row.by}</td>
                              <td>{formatDateTime(row.timeStamp)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .unreleased-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .unreleased-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .unreleased-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .unreleased-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .unreleased-search-group {
          width: 100%;
        }

        .unreleased-action-btn {
          border-radius: 0.65rem;
          font-weight: 600;
          padding: 0.42rem 0.8rem;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .unreleased-utility-btn {
          min-width: 110px;
        }

        .unreleased-btn-icon {
          font-size: 0.95rem;
        }

        .unreleased-queue-badge {
          display: inline-flex;
          align-items: center;
          min-height: 38px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 0.58rem 0.8rem;
          border-radius: 0.65rem;
          font-size: 0.85rem;
          color: #0f172a;
          background: #f8fafc;
        }

        .unreleased-info-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          min-height: 38px;
          padding: 0.45rem 0.75rem;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 0.65rem;
          background: #f8fafc;
          max-width: 300px;
        }

        .unreleased-info-label {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          color: #64748b;
          white-space: nowrap;
        }

        .unreleased-info-value {
          font-size: 0.9rem;
          font-weight: 700;
          color: #0f172a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .unreleased-refresh-note {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.2rem;
          font-size: 0.84rem;
          color: #64748b;
          font-weight: 500;
        }

        .btn-soft-dark {
          background: rgba(15, 23, 42, 0.08);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: rgba(15, 23, 42, 0.14);
          color: #0f172a;
          border-color: rgba(15, 23, 42, 0.12);
        }

        .unreleased-table-shell {
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 1rem;
          overflow: hidden;
          background: #fff;
        }

        .unreleased-table-scroll {
          max-height: 540px;
          overflow: auto;
        }

        .unreleased-table {
          min-width: 980px;
        }

        .unreleased-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #0f172a;
          color: #f8fafc;
          font-size: 0.74rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 800;
          padding: 0.95rem 0.9rem;
          border-bottom: 0;
          white-space: nowrap;
        }

        .unreleased-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #334155;
          vertical-align: middle;
          white-space: nowrap;
        }

        .unreleased-col-select {
          width: 72px;
          text-align: center;
        }

        .unreleased-data-row {
          transition: background-color 0.18s ease, box-shadow 0.18s ease;
          cursor: pointer;
        }

        .unreleased-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .unreleased-data-row:hover {
          background: #f5f9ff;
        }

        .unreleased-data-row.selected {
          background: rgba(13, 110, 253, 0.08);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        @media (max-width: 1199.98px) {
          .unreleased-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .unreleased-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .unreleased-table-scroll {
            max-height: 460px;
          }

          .unreleased-info-pill {
            max-width: 100%;
          }

          .unreleased-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function getRowKey(row) {
  return `${row.partNumber ?? ""}-${row.printRevision ?? 0}-${row.mafiaRevision ?? 0}`;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

async function getUnreleasedParts() {
  const response = await api.get("mafia/unreleased-parts");

  return (response.data || []).map((item) => ({
    partNumber: item.partNumber,
    printRevision: item.printRevision,
    mafiaRevision: item.mafiaRevision,
    by: item.by,
    timeStamp: item.timeStamp,
  }));
}